import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';
import { useCart } from '../../contexts/CartContext';
import { toast } from 'react-toastify';
import SEO from '../../components/common/SEO';
import PaymentMethodSelector from '../../components/ui/PaymentMethodSelector';
import OrderSummary from '../../components/ui/OrderSummary';
import { isUserInTunisia, getUserCountryInfo } from '../../utils/geolocation';
import useExchangeRate from '../../hooks/useExchangeRate';

console.log('CheckoutPage mounted');

const validateEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};
const validateName = (name) => {
  return /^[A-Za-zÀ-ÿ\-\s]{2,50}$/.test(name);
};
const validatePhone = (phone) => {
  return /^\+?\d{8,15}$/.test(phone);
};
const validateAddress = (address) => {
  return address && address.length >= 5 && address.length <= 100;
};
const validateCity = (city) => {
  return /^[A-Za-zÀ-ÿ\s]{2,50}$/.test(city);
};
const validatePostalCode = (postalCode) => {
  return /^\d{3,10}$/.test(postalCode);
};
const validateCountry = (country) => {
  return country && country.length >= 2;
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { items, getCartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [geolocationLoading, setGeolocationLoading] = useState(true);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('flouci');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: ''
  });
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const TND_EXCHANGE_RATE = useExchangeRate();
  const shippingCountry = formData.country?.trim();
  const isTunisianUser = shippingCountry &&
    (shippingCountry.toLowerCase() === 'tunisia' || shippingCountry.toLowerCase() === 'tunisie');
  const [flouciStatus, setFlouciStatus] = useState(null);
  const [flouciStatusLoading, setFlouciStatusLoading] = useState(false);
  const [flouciStatusError, setFlouciStatusError] = useState('');
  const [shippingError, setShippingError] = useState('');
  const [shipping, setShipping] = useState(0);
  const [shippingLoading, setShippingLoading] = useState(false);
  const shippingFetchTimeout = useRef(null);

  // Fetch available countries from shipping fees API
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/shipping-fees`);
        if (response.ok) {
          const data = await response.json();
          // Map 'Tunisie' to 'Tunisia' for frontend
          const uniqueCountries = [...new Set(
            data.map(fee => fee.country === 'Tunisie' ? 'Tunisia' : fee.country)
          )].sort();
          setCountries(uniqueCountries);
        } else {
          console.error('Failed to fetch countries');
          setCountries(['Tunisia', 'France', 'Germany', 'Italy', 'Spain', 'United States', 'United Kingdom', 'Canada']);
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
        setCountries(['Tunisia', 'France', 'Germany', 'Italy', 'Spain', 'United States', 'United Kingdom', 'Canada']);
      } finally {
        setCountriesLoading(false);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    console.log('Geolocation effect running');
    const detectLocation = async () => {
      try {
        const countryInfo = await getUserCountryInfo();
        setFormData(prev => ({
          ...prev,
          country: prev.country || (countryInfo.country === 'Tunisie' ? 'Tunisia' : countryInfo.country)
        }));
      } catch (error) {
        console.error('Failed to detect user location:', error);
      } finally {
        setGeolocationLoading(false);
      }
    };

    detectLocation();
  }, []);

  useEffect(() => {
    if (items.length === 0) {
      navigate('/cart');
      toast.info('Your cart is empty');
    }
  }, [items, navigate]);

  // Check for Flouci sessionId in URL after redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('sessionId');
    if (sessionId) {
      setFlouciStatusLoading(true);
      fetch(`${import.meta.env.VITE_API_BASE_URL}/api/checkout/session/${sessionId}`)
        .then(res => res.json())
        .then(data => {
          setFlouciStatus(data.status || 'unknown');
        })
        .catch(() => setFlouciStatusError('Failed to check payment status.'))
        .finally(() => setFlouciStatusLoading(false));
    }
  }, [location.search]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  }, []);

  const validateAll = () => {
    const newErrors = {};
    if (!validateName(formData.firstName)) newErrors.firstName = 'Enter a valid first name (letters, spaces, hyphens, 2-50 chars).';
    if (!validateName(formData.lastName)) newErrors.lastName = 'Enter a valid last name (letters, spaces, hyphens, 2-50 chars).';
    if (!validateEmail(formData.email)) newErrors.email = 'Enter a valid email address.';
    if (formData.phone && !validatePhone(formData.phone)) newErrors.phone = 'Enter a valid phone number (8-15 digits, optional +).';
    if (!validateAddress(formData.address)) newErrors.address = 'Enter a valid address (5-100 chars).';
    if (!validateCity(formData.city)) newErrors.city = 'Enter a valid city (letters and spaces, 2-50 chars).';
    if (!validatePostalCode(formData.postalCode)) newErrors.postalCode = 'Enter a valid postal code (3-10 digits).';
    if (!validateCountry(formData.country)) newErrors.country = 'Enter a valid country.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePaymentMethodChange = useCallback((method) => {
    setSelectedPaymentMethod(method);
  }, []);

  const processFlouciPayment = async (orderData) => {
    setLoading(true);
    setShippingError('');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/checkout/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.url) {
          window.location.href = data.url;
        } else {
          toast.error('Failed to initiate Flouci payment.');
        }
      } else {
        const errorData = await response.json();
        if (errorData.error && errorData.error.toLowerCase().includes('shipping fee')) {
          setShippingError(errorData.error);
        } else {
          toast.error('Failed to initiate Flouci payment.');
        }
      }
    } catch (error) {
      toast.error('Payment error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const processPayOnDelivery = async (orderData) => {
    setLoading(true);
    try {
      // Send order to backend for pay on delivery
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/pay-on-delivery-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData),
      });
      if (!response.ok) throw new Error('Order failed');
      const result = await response.json();
      if (!result.success) throw new Error('Order failed');
      // Decrease product quantities
      await decreaseProductQuantities(orderData);
      clearCart();
      navigate(`/success?payment_method=pay_on_delivery&total=${displayTotal.toFixed(2)}`);
      toast.success('Order placed successfully! You will pay upon delivery.');
    } catch (error) {
      console.error('Error processing pay on delivery order:', error);
      toast.error('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll()) {
      toast.error('Please fix the errors in the form.');
      return;
    }
    setLoading(true);
    try {
      const orderData = {
        items: items.map(item => ({
          productId: item.productId || item.id || item._id,
          id: item.id || item._id,
          title: item.title || item.name,
          price: parseFloat(item.price),
          quantity: item.quantity,
          weight: item.weight,
          variantIndex: item.variantIndex
        })),
        customerInfo: formData,
        total: getCartTotal(),
        paymentMethod: selectedPaymentMethod === 'flouci' ? 'online' : selectedPaymentMethod,
        userLocation: {
          country: shippingCountry,
          isTunisia: isTunisianUser
        }
      };
      if (selectedPaymentMethod === 'flouci') {
        await processFlouciPayment(orderData);
      } else if (selectedPaymentMethod === 'pay_on_delivery') {
        await processPayOnDelivery(orderData);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const subtotal = getCartTotal();
  
  // Memoize the order summary props to prevent unnecessary re-renders
  const orderSummaryProps = useMemo(() => ({
    items,
    isTunisianUser,
    TND_EXCHANGE_RATE,
    shipping,
    subtotal
  }), [items, isTunisianUser, TND_EXCHANGE_RATE, shipping, subtotal]);

  // Helper to format currency
  const formatCurrency = useCallback((amount) => {
    if (isTunisianUser) {
      return `${amount.toFixed(2)} TND`;
    } else {
      return `€${amount.toFixed(2)}`;
    }
  }, [isTunisianUser]);

  async function decreaseProductQuantities(orderData) {
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    for (const item of orderData.items) {
      try {
        const res = await fetch(`${baseUrl}/api/products/${item.productId}/variant/${item.variantIndex}/decrease`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount: item.quantity }),
        });
        const data = await res.json();
        if (!res.ok) {
          console.error('Failed to decrease stock:', data);
        }
      } catch (err) {
        console.error('Error in decreaseProductQuantities:', err);
      }
    }
  }

  // Debounced shipping fee fetch
  const fetchShippingFee = (country) => {
    if (!country) {
      setShipping(0);
      setShippingError('');
      return;
    }
    setShippingLoading(true);
    setShippingError('');
    fetch(`${import.meta.env.VITE_API_BASE_URL}/api/shipping-fees?country=${encodeURIComponent(country)}`)
      .then(res => res.json())
      .then(data => {
        if (typeof data.fee === 'number') {
          setShipping(data.fee);
          setShippingError('');
        } else if (data.error) {
          setShipping(0);
          setShippingError(data.error);
        } else {
          setShipping(0);
          setShippingError('Shipping is not available for this country.');
        }
      })
      .catch(() => {
        setShipping(0);
        setShippingError('Shipping is not available for this country.');
      })
      .finally(() => setShippingLoading(false));
  };

  // Debounce shipping fee fetch on country change
  useEffect(() => {
    if (shippingFetchTimeout.current) {
      clearTimeout(shippingFetchTimeout.current);
    }
    if (shippingCountry) {
      shippingFetchTimeout.current = setTimeout(() => {
        fetchShippingFee(shippingCountry);
      }, 600);
    } else {
      setShipping(0);
      setShippingError('');
    }
    return () => {
      if (shippingFetchTimeout.current) {
        clearTimeout(shippingFetchTimeout.current);
      }
    };
  }, [shippingCountry]);

  if (items.length === 0) {
    return null;
  }

  if (isTunisianUser && !TND_EXCHANGE_RATE) {
    return <div>Loading exchange rate...</div>;
  }

  // At the top of the return, show Flouci payment status if present
  if (flouciStatusLoading) {
    return <div className="text-center py-10 text-lg">Checking payment status...</div>;
  }
  if (flouciStatus) {
    if (flouciStatus === 'success') {
      return <div className="text-center py-10 text-green-600 text-xl font-bold">Payment successful! Thank you for your order.</div>;
    } else if (flouciStatus === 'pending') {
      return <div className="text-center py-10 text-yellow-600 text-xl font-bold">Payment pending. Please wait or check your payment app.</div>;
    } else {
      return <div className="text-center py-10 text-red-600 text-xl font-bold">Payment failed or cancelled. Please try again.</div>;
    }
  }
  if (flouciStatusError) {
    return <div className="text-center py-10 text-red-600 text-lg">{flouciStatusError}</div>;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Checkout"
        description="Complete your Elixir honey purchase. Secure checkout for premium Mediterranean honey varieties with worldwide shipping."
        keywords="Elixir checkout, honey purchase, Mediterranean honey order, secure honey checkout, premium honey shipping"
      />
      <Header />
      <main className="flex-grow container mx-auto px-4 py-10">
        <h1 className="text-3xl font-bold mb-8">Checkout</h1>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Left: Checkout Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-white shadow-md border border-gray-200 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-3 py-2 border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500`}
                    />
                    {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-3 py-2 border ${errors.lastName ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500`}
                    />
                    {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500`}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-3 py-2 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500`}
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>
              </div>
              <div className="bg-white shadow-md border border-gray-200 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Shipping Address</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address *
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-3 py-2 border ${errors.address ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500`}
                    />
                    {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-3 py-2 border ${errors.city ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500`}
                      />
                      {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Postal Code *
                      </label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-3 py-2 border ${errors.postalCode ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500`}
                      />
                      {errors.postalCode && <p className="text-red-500 text-xs mt-1">{errors.postalCode}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Country *
                      </label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        required
                        disabled={countriesLoading || geolocationLoading}
                        className={`w-full px-3 py-2 border ${errors.country ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 ${(countriesLoading || geolocationLoading) ? 'bg-gray-100' : ''}`}
                      >
                        <option value="">
                          {countriesLoading ? 'Loading countries...' : geolocationLoading ? 'Detecting your location...' : 'Select a country'}
                        </option>
                        {countries.map((country) => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                      {(countriesLoading || geolocationLoading) && (
                        <span className="text-sm text-gray-500">
                          {countriesLoading ? 'Loading available countries...' : 'Detecting your location...'}
                        </span>
                      )}
                      {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country}</p>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white shadow-md border border-gray-200 rounded-lg p-6">
                <PaymentMethodSelector
                  selectedMethod={selectedPaymentMethod}
                  onMethodChange={handlePaymentMethodChange}
                  isTunisianUser={isTunisianUser}
                  loading={geolocationLoading}
                />
              </div>
              {shippingError && (
                <div className="mb-6 p-4 bg-red-100 border border-red-300 text-red-800 rounded text-center text-base font-semibold">
                  {shippingError} <br />
                  If you believe this is a mistake or want to request shipping to your country, please <a href="mailto:team@elixir-honey.com" className="underline text-red-700">contact us</a>.
                </div>
              )}
              <button
                type="submit"
                disabled={loading || geolocationLoading || countriesLoading}
                className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 text-white font-medium py-3 rounded transition"
              >
                {loading ? 'Processing...' : `Complete Order - ${formatCurrency(isTunisianUser ? (subtotal * TND_EXCHANGE_RATE + (shipping || 0)) : (subtotal + (shipping || 0)))}`}
              </button>
            </form>
          </div>
          {/* Right: Order Summary */}
          <OrderSummary {...orderSummaryProps} shippingLoading={shippingLoading} geolocationLoading={geolocationLoading} />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CheckoutPage; 